<template>
  <div
    v-if="visible"
    class="fixed inset-0 z-50 flex items-center justify-center"
    :class="[
      overlay ? 'bg-black/20 backdrop-blur-sm' : '',
      'transition-all duration-300'
    ]"
  >
    <div
      class="flex flex-col items-center space-y-4"
      :class="[
        size === 'small'
          ? 'space-y-3'
          : size === 'large'
            ? 'space-y-5'
            : 'space-y-4'
      ]"
    >
      <!-- 旋转动画 - 添加渐变色和阴影效果 -->
      <div class="relative">
        <!-- 外层光圈效果 -->
        <div
          class="absolute inset-0 rounded-full animate-pulse opacity-30"
          :class="[
            size === 'small'
              ? 'w-8 h-8'
              : size === 'large'
                ? 'w-16 h-16'
                : 'w-12 h-12'
          ]"
          :style="{
            background: `radial-gradient(circle, ${color}20, transparent 70%)`,
            filter: 'blur(4px)'
          }"
        ></div>

        <!-- 主要旋转圆环 -->
        <div
          class="relative animate-spin rounded-full border-4 border-transparent"
          :class="[
            spinnerClass,
            size === 'small'
              ? 'h-6 w-6 border-2'
              : size === 'large'
                ? 'h-12 w-12 border-4'
                : 'h-8 w-8 border-3'
          ]"
          :style="{
            background: `conic-gradient(from 0deg, ${color}, ${color}80, transparent, transparent, ${color})`,
            borderRadius: '50%'
          }"
        >
          <!-- 内层白色圆心 -->
          <div
            class="absolute inset-1 bg-white rounded-full shadow-sm"
            :class="[
              size === 'small'
                ? 'inset-0.5'
                : size === 'large'
                  ? 'inset-1.5'
                  : 'inset-1'
            ]"
          ></div>
        </div>
      </div>

      <!-- 加载文本 - 优化样式 -->
      <div v-if="text" class="relative text-center animate-fade-in">
        <!-- 文字背景光效 -->
        <div
          class="absolute inset-0 rounded-full opacity-60 blur-sm"
          :style="{
            background: `linear-gradient(135deg, ${color}15, ${color}05)`
          }"
        ></div>

        <!-- 主要文字 -->
        <p
          class="relative text-white font-medium px-4 py-2 bg-gray-800/30 rounded-full backdrop-blur-md border border-white/10"
          :class="[
            size === 'small'
              ? 'text-xs px-3 py-1.5'
              : size === 'large'
                ? 'text-base px-6 py-3'
                : 'text-sm px-4 py-2'
          ]"
          :style="{
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            boxShadow: `0 4px 12px ${color}20, inset 0 1px 0 rgba(255,255,255,0.1)`
          }"
        >
          {{ text }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  /** 是否显示loading */
  visible?: boolean
  /** 加载文本 */
  text?: string
  /** 主题颜色 */
  color?: string
  /** 尺寸 */
  size?: 'small' | 'medium' | 'large'
  /** 是否显示遮罩层 */
  overlay?: boolean
  /** 自定义spinner样式类 */
  spinnerClass?: string
}

withDefaults(defineProps<Props>(), {
  visible: false,
  text: '加载中...',
  color: '#3b82f6',
  size: 'medium',
  overlay: true,
  spinnerClass: ''
})
</script>

<style scoped>
/* 确保在最顶层显示 */
.fixed {
  position: fixed;
}

/* 优化的旋转动画 */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.05);
  }
}

@keyframes gradient-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: gradient-spin 1.2s linear infinite;
}

.animate-pulse {
  animation: pulse-glow 2s ease-in-out infinite;
}

/* 增强的毛玻璃效果 */
.backdrop-blur-md {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* 自定义边框宽度 */
.border-3 {
  border-width: 3px;
}

/* 渐变边框效果 */
.gradient-border {
  position: relative;
  background: linear-gradient(45deg, transparent, transparent);
}

.gradient-border::before {
  content: '';
  position: absolute;
  inset: 0;
  padding: 3px;
  background: conic-gradient(
    from 0deg,
    var(--primary-color),
    transparent,
    var(--primary-color)
  );
  border-radius: 50%;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

/* 平滑过渡效果 */
.transition-all {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 文字渐现动画 */
@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in-up 0.5s ease-out 0.3s both;
}
</style>
